import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { IWebauthnCredentialItem } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { isPasskeyCancellation } from "tools/auth/isPasskeyCancellation";
import { useTwoFactorSettings } from "store/two-factor/two-factor-settings.store";
import { registerStore } from "store/utils/reset-store.utils";

const PASSKEY_CANCELLED_MESSAGE_KEY =
  "common.two_factor.passkey.setup_cancelled";

const PASSKEY_ERROR_MESSAGE_KEY = "common.two_factor.passkey.add_failed";

type State = {
  credentials: IWebauthnCredentialItem[] | null;
  isRegistering: boolean;
  registerErrorKey: string | null;
  recoveryCodes: string[] | null;
};

type Action = {
  fetchCredentials: () => Promise<void>;
  requestRegistrationOptions: (
    password?: string
  ) => Promise<PublicKeyCredentialCreationOptionsJSON>;
  completeAddPasskey: (
    options: PublicKeyCredentialCreationOptionsJSON
  ) => Promise<void>;
  revokeCredential: (id: string, password?: string) => Promise<void>;
  clearRegisterError: () => void;
  clearRecoveryCodes: () => void;
};

export const usePasskeys = create(
  devtools<State & Action>(
    (set, get) => ({
      credentials: null,
      isRegistering: false,
      registerErrorKey: null,
      recoveryCodes: null,

      fetchCredentials: async () => {
        try {
          const credentials = await apiV2.webauthn.getCredentials();
          set({ credentials });
        } catch (error) {
          console.error("Failed to fetch passkeys:", error);
          set({ credentials: [] });
        }
      },

      requestRegistrationOptions: async (password) => {
        set({ registerErrorKey: null });
        return apiV2.webauthn.getRegistrationOptions({ password });
      },

      completeAddPasskey: async (options) => {
        set({ isRegistering: true });
        try {
          const credential = await startRegistration({ optionsJSON: options });
          const { recoveryCodes } = await apiV2.webauthn.verifyRegistration({
            credential,
          });
          set({ recoveryCodes: recoveryCodes?.length ? recoveryCodes : null });
          await get().fetchCredentials();
          useTwoFactorSettings.getState().setMethodConfigured("passkey", true);
        } catch (error) {
          set({
            registerErrorKey: isPasskeyCancellation(error)
              ? PASSKEY_CANCELLED_MESSAGE_KEY
              : PASSKEY_ERROR_MESSAGE_KEY,
          });
        } finally {
          set({ isRegistering: false });
        }
      },

      revokeCredential: async (id, password) => {
        await apiV2.webauthn.deleteCredential({ id }, { password });
        await get().fetchCredentials();
        useTwoFactorSettings
          .getState()
          .setMethodConfigured("passkey", (get().credentials?.length ?? 0) > 0);
      },

      clearRegisterError: () => set({ registerErrorKey: null }),
      clearRecoveryCodes: () => set({ recoveryCodes: null }),
    }),
    { name: "passkey" }
  )
);

registerStore(usePasskeys);
